import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Star, ArrowRight, Zap } from "lucide-react";
import { specializationsOf } from "@shared/specializations";
import { photoSrc } from "@shared/photoSrc";

function RecommendedLawyerCard({ lawyer, onBook, onViewProfile }) {
  const displayName = lawyer.name || "Advoset Lawyer";
  const initials = displayName.split(" ").slice(-2).map((x) => x[0] || "").join("") || "AL";
  const available = !!(lawyer.instant_available ?? lawyer.available);
  const isPulsing = !!lawyer.pulse_until && new Date(lawyer.pulse_until).getTime() > Date.now();
  const tags = specializationsOf(lawyer).slice(0, 3);
  const primarySpec = lawyer.specialization || tags[0] || "Legal consultation";

  return (
    <div
      data-testid={`recommended-lawyer-${lawyer.id}`}
      className="group bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-start gap-3">
        {lawyer.photo ? (
          <img
            src={photoSrc(lawyer.photo, "sm")}
            alt={displayName}
            className="h-14 w-14 rounded-full object-cover border border-slate-200 shrink-0"
          />
        ) : (
          <div className="h-14 w-14 rounded-full adv-navy text-[#D4AF37] grid place-items-center font-bold text-lg shrink-0">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3
              className="font-bold text-[#0A2540] text-sm leading-tight truncate"
              style={{ fontFamily: "Poppins" }}
            >
              {displayName}
            </h3>
            {(isPulsing || available) ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-600" />
                </span>
                Online
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Offline
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">{primarySpec}</p>
          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-600 flex-wrap">
            <span className="inline-flex items-center gap-0.5 bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded font-bold">
              <Star size={10} fill="currentColor" />
              {Number(lawyer.rating || 0).toFixed(1)}
            </span>
            <span className="text-slate-500">({lawyer.reviews_count || 0})</span>
            <span className="text-slate-500">· {Number(lawyer.experience || 0)}+ years</span>
          </div>
        </div>
      </div>

      {tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span
              key={tag}
              className="text-[10px] font-semibold text-slate-600 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5 truncate max-w-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4">
        {available ? (
          <Button
            onClick={() => onBook(lawyer)}
            className="w-full rounded-full adv-btn-gold font-bold h-10"
            data-testid={`recommended-consult-${lawyer.id}`}
          >
            {isPulsing && <Zap size={13} fill="currentColor" className="mr-1" />}
            Consult Now
          </Button>
        ) : (
          <Button
            onClick={() => onViewProfile(lawyer)}
            variant="outline"
            className="w-full rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold h-10"
            data-testid={`recommended-view-${lawyer.id}`}
          >
            View Profile
          </Button>
        )}
      </div>
    </div>
  );
}

function RecommendedSkeleton() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-start gap-3">
        <div className="h-14 w-14 rounded-full adv-skeleton shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 rounded adv-skeleton w-3/4" />
          <div className="h-2.5 rounded adv-skeleton w-1/2" />
          <div className="h-2.5 rounded adv-skeleton w-2/3" />
        </div>
      </div>
      <div className="mt-3 flex gap-1.5">
        <div className="h-5 rounded-full adv-skeleton w-16" />
        <div className="h-5 rounded-full adv-skeleton w-14" />
        <div className="h-5 rounded-full adv-skeleton w-12" />
      </div>
      <div className="mt-4 h-10 rounded-full adv-skeleton w-full" />
    </div>
  );
}

export function RecommendedLawyers({ lawyers, loading, error, onBook, onViewProfile }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2
            className="text-2xl lg:text-3xl font-extrabold text-[#0A2540]"
            style={{ fontFamily: "Poppins" }}
          >
            Available Lawyers
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Verified lawyers available for consultation now
          </p>
        </div>
        <Link
          to="/lawyers"
          className="hidden md:inline-flex text-sm font-bold text-[#1E3A8A] hover:text-[#0A2540] items-center gap-1"
        >
          View all lawyers <ArrowRight size={14} />
        </Link>
      </div>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <RecommendedSkeleton key={i} />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <p className="text-sm text-slate-500">Could not load lawyer recommendations right now.</p>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="mt-3 rounded-full font-semibold text-xs h-9"
          >
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && lawyers.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <p className="text-sm text-slate-500">No lawyers are available right now.</p>
          <Link to="/lawyers">
            <Button className="mt-3 rounded-full adv-btn-gold font-bold text-xs h-9">
              Browse all lawyers
            </Button>
          </Link>
        </div>
      )}

      {!loading && !error && lawyers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="recommended-lawyers-grid">
          {lawyers.map((l) => (
            <RecommendedLawyerCard
              key={l.id}
              lawyer={l}
              onBook={onBook}
              onViewProfile={onViewProfile}
            />
          ))}
        </div>
      )}

      <div className="mt-4 md:hidden text-right">
        <Link
          to="/lawyers"
          className="inline-flex text-sm font-bold text-[#1E3A8A] hover:text-[#0A2540] items-center gap-1"
        >
          View all lawyers <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

export default RecommendedLawyers;
